import { formatCurrency } from "../../../../../utils/formatCurrency";
import { escapeHtml } from "./securityHelpers";

/**
 * Generates the complete HTML for invoice printing
 * @param {Object} data - Invoice data object
 * @param {Function} t - Translation function
 * @param {Boolean} isPreview - If true, disables auto-print
 * @returns {String} Complete HTML document
 */
export const generateInvoiceHTML = (data, t, isPreview = false) => {
  // Extract lang early for error handling
  const lang = data?.lang || 'ar';
  
  try {
    // Validate required data
    if (!data.orderData || !data.order) {
      throw new Error('Order data is missing');
    }

    const {
      orderData,
      order,
      orderProducts,
      totalRefudProductsPrice,
      siteTitle,
      invoiceLogoUrl,
      companyEmail,
      companyPhone,
      formattedDate,
      isRTL,
      switchOrderStatus,
      handleShowTitle,
      switchLangLoading
    } = data;

    // Generate sections
    const customerInfo = generateCustomerInfo(orderData, t);
    const shippingInfo = generateShippingInfo(orderData, t);
    const productsRows = generateProductsRows(orderProducts, t, switchLangLoading, handleShowTitle);
    const paymentsTable = generatePaymentsTable(orderData, t);
    const footer = generateFooter(siteTitle, companyEmail, companyPhone, orderData, t, isRTL);
    const styles = generateStyles(isRTL);

    // Generate invoice filename
    const invoiceNumber = orderData?.invoice_no || orderData?.id || 'unknown';
    const invoiceDate = formattedDate.replace(/\s*\/\s*/g, '-'); // Replace / with -
    const filename = `Invoice-${invoiceNumber}-${invoiceDate}`.replace(/\s+/g, '_');

    return `
      <!DOCTYPE html>
      <html lang="${lang}" dir="${isRTL ? 'rtl' : 'ltr'}">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${filename}</title>
        <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.1.3/dist/css/bootstrap.min.css" rel="stylesheet">
        <style>${styles}</style>
      </head>
      <body>
        <div class="invoice-print-sheet">
        ${generateInvoiceHeader(orderData, order, siteTitle, invoiceLogoUrl, companyEmail, companyPhone, formattedDate, switchOrderStatus, t, isRTL)}
        ${generateInfoCards(siteTitle, companyEmail, companyPhone, customerInfo, shippingInfo, t)}
        ${generateProductsTable(productsRows, t)}
        ${generateSummaryTable(order, totalRefudProductsPrice, t)}
        ${paymentsTable}
        ${footer}
        </div>
        <script>
          // Set document title for better filename when saving/printing
          document.title = '${filename}';
          
          ${isPreview ? 
            '// Preview mode - no auto-print' : 
            `window.onload = function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            };`
          }
        </script>
      </body>
      </html>
    `;
  } catch (error) {
    console.error('Error generating print content:', error);
    return generateErrorPage(t, lang);
  }
};

/**
 * Generate customer information section
 */
const generateCustomerInfo = (orderData, t) => {
  const safeText = (value, fallback = '') => escapeHtml(value || fallback);
  
  return orderData?.user ? `
    <div class="fw-semibold mb-1">${safeText(orderData.user.first_name)} ${safeText(orderData.user.last_name)}</div>
    <div class="text-muted">${safeText(orderData.user.email)}</div>
    ${orderData.user.phone ? `<div class="text-muted">${safeText(orderData.user.phone)}</div>` : ''}
  ` : `
    <div class="fw-semibold mb-1">${safeText(orderData?.guest_name, t("Guest Customer"))}</div>
    <div class="text-muted">${safeText(orderData?.guest_email, t("Not provided"))}</div>
    ${orderData?.phone ? `<div class="text-muted">${safeText(orderData.phone)}</div>` : ''}
  `;
};

/**
 * Generate shipping information section
 */
const generateShippingInfo = (orderData, t) => {
  const safeText = (value) => escapeHtml(value || '');
  
  if (!orderData?.shipping_address) {
    return `<div class="text-muted">${t("No shipping address provided")}</div>`;
  }

  const { address, city, state, country } = orderData.shipping_address;
  
  return `
    ${address ? `<div class="text-muted">${safeText(address)}</div>` : ''}
    <div class="text-muted">${[safeText(city), safeText(state)].filter(Boolean).join(", ")}</div>
    ${country ? `<div class="text-muted">${safeText(country)}</div>` : ''}
  `;
};

/**
 * Generate products table rows
 */
const generateProductsRows = (orderProducts, t, switchLangLoading, handleShowTitle) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return orderProducts.map((product, index) => {
    const matchedVariant = product?.variants?.find(
      (variant) => variant.id == product?.pivot?.product_variant_id
    );
    
    const productTitle = switchLangLoading 
      ? safeText(t("Loading")) + '...' 
      : safeText(handleShowTitle(product.translations));
    
    const variantInfo = matchedVariant ? `
      <div class="text-muted small">${safeText(matchedVariant.sku)}</div>
      ${matchedVariant.attribute_values && matchedVariant.attribute_values.length > 0 ? 
        `<div class="text-muted small">${matchedVariant.attribute_values.map(attr => safeText(attr.value)).join(' / ')}</div>` 
        : ''}
    ` : `<span class="text-muted">${t("No Variant")}</span>`;

    return `
      <tr>
        <td style="text-align: center;">${index + 1}</td>
        <td>
          <div class="fw-semibold">${productTitle}</div>
          <div class="text-muted small">${safeText(product.sku)}</div>
        </td>
        <td>${variantInfo}</td>
        <td style="text-align: center;">${product.pivot?.qty || 0}</td>
        <td style="text-align: right;">${formatCurrency(product.pivot?.price || 0)}</td>
        <td style="text-align: center;">${parseInt(product.pivot?.refund_qty || 0) >= 1 ? `-${product.pivot.refund_qty}` : t("No Refund")}</td>
        <td style="text-align: right;">${formatCurrency(parseInt(product.pivot?.qty || 0) * parseInt(product.pivot?.price || 0))}</td>
      </tr>
    `;
  }).join('');
};

/**
 * Generate payments table
 */
const generatePaymentsTable = (orderData, t) => {
  if (!orderData?.payments || orderData.payments.length === 0) {
    return '';
  }

  const paymentsRows = orderData.payments.map(payment => `
    <tr>
      <td style="padding: 5px 6px; font-size: 9pt;">${escapeHtml(payment.payment_gateway || t("Unknown"))}</td>
      <td style="padding: 5px 6px; font-size: 9pt;"><code style="font-size: 8pt;">${escapeHtml(payment.gateway_transaction_id || t("N/A"))}</code></td>
      <td style="padding: 5px 6px; font-size: 9pt; text-align: right;">${formatCurrency(parseFloat(payment.amount || 0))}</td>
      <td style="padding: 5px 6px; font-size: 9pt; text-align: center;">${escapeHtml(payment.currency || t("N/A"))}</td>
      <td style="padding: 5px 6px; font-size: 9pt; text-align: center;">${escapeHtml(payment.status || t("Unknown"))}</td>
      <td style="padding: 5px 6px; font-size: 9pt; text-align: center;">${payment.created_at ? new Date(payment.created_at).toLocaleDateString() : t("N/A")}</td>
    </tr>
  `).join('');

  return `
    <hr style="border: 0; border-top: 1px solid #ddd; margin: 14px 0;" />
    <div class="fw-bold mb-2 text-uppercase" style="font-size: 10pt;">${t("Payment Information")}</div>
    <table class="invoice-payments-table" style="width: 100%; border-collapse: collapse; font-size: 9pt;">
      <thead>
        <tr>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Payment Gateway")}</th>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Transaction ID")}</th>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Amount")}</th>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Currency")}</th>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Status")}</th>
          <th style="padding: 6px 8px; background: #f5f5f5; font-size: 9pt;">${t("Date")}</th>
        </tr>
      </thead>
      <tbody>
        ${paymentsRows}
      </tbody>
    </table>
  `;
};

/**
 * Generate invoice header section
 */
const generateInvoiceHeader = (orderData, order, siteTitle, invoiceLogoUrl, companyEmail, companyPhone, formattedDate, switchOrderStatus, t, isRTL) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div class="invoice-header">
      <div class="company-info">
        <img src="${invoiceLogoUrl}" alt="Logo" onerror="this.style.display='none'" />
        <h4 class="fw-bold mb-1">${safeText(siteTitle)}</h4>
        <div class="text-muted" style="font-size: 9pt;">
          <div>${safeText(companyEmail)}</div>
          <div>${safeText(companyPhone)}</div>
        </div>
      </div>
      <div class="invoice-info">
        <h2>${t("Invoice")}</h2>
        <div style="margin-bottom: 4px; font-size: 10pt;"><strong>${t("Invoice No")}:</strong> #${safeText(orderData?.invoice_no || orderData?.id)}</div>
        <div style="margin-bottom: 8px; font-size: 10pt;"><strong>${t("Date")}:</strong> ${safeText(formattedDate)}</div>
        <div class="invoice-status-row">
          <span class="status-label">${t("Order Status")}:</span>
          <span class="badge badge-order">${switchOrderStatus(order.status)}</span>
          <span class="status-label" style="margin-inline-start: 12px;">${t("Payment Status")}:</span>
          <span class="badge badge-payment">${safeText(orderData?.payment_status || t("Unknown"))}</span>
        </div>
      </div>
    </div>
  `;
};

/**
 * Generate info cards section
 */
const generateInfoCards = (siteTitle, companyEmail, companyPhone, customerInfo, shippingInfo, t) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div class="info-cards">
      <div class="info-card">
        <h6>${t("From")}</h6>
        <div class="fw-semibold mb-1">${safeText(siteTitle)}</div>
        <div class="text-muted" style="font-size: 9pt;">${safeText(companyEmail)}</div>
        <div class="text-muted" style="font-size: 9pt;">${safeText(companyPhone)}</div>
      </div>
      <div class="info-card">
        <h6>${t("Bill To")}</h6>
        <div style="font-size: 9pt;">${customerInfo}</div>
      </div>
      <div class="info-card">
        <h6>${t("Shipping Address")}</h6>
        <div style="font-size: 9pt;">${shippingInfo}</div>
      </div>
    </div>
  `;
};

/**
 * Generate products table
 */
const generateProductsTable = (productsRows, t) => {
  return `
    <table class="invoice-products-table">
      <thead>
        <tr>
          <th style="width: 4%; text-align: center;">#</th>
          <th style="width: 26%;">${t("Item")}</th>
          <th style="width: 18%;">${t("Variant")}</th>
          <th style="width: 8%; text-align: center;">${t("Quantity")}</th>
          <th style="width: 12%; text-align: right;">${t("Price")}</th>
          <th style="width: 10%; text-align: center;">${t("Refund")}</th>
          <th style="width: 12%; text-align: right;">${t("Total")}</th>
        </tr>
      </thead>
      <tbody>
        ${productsRows}
      </tbody>
    </table>
  `;
};

/**
 * Generate summary table
 */
const generateSummaryTable = (order, totalRefudProductsPrice, t) => {
  return `
    <table class="summary-table">
      <tbody>
        <tr>
          <td class="fw-semibold">${t("Subtotal")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.total_price || 0))}</td>
        </tr>
        <tr>
          <td>${t("Discount")}</td>
          <td style="text-align: right;">-${formatCurrency(parseFloat(order.discount) || 0)}</td>
        </tr>
        <tr>
          <td>${t("Shipping")}</td>
          <td style="text-align: right;">${parseFloat(order.shipping_amount || 0) >= 1 ? formatCurrency(parseFloat(order.shipping_amount)) : '-'}</td>
        </tr>
        <tr>
          <td>${t("TAX")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.tax_fee_amount || 0))}</td>
        </tr>
        ${parseFloat(totalRefudProductsPrice) > 0 ? `
          <tr>
            <td>${t("Total Refund")}</td>
            <td style="text-align: right; color: #dc3545;">-${formatCurrency(parseFloat(totalRefudProductsPrice))}</td>
          </tr>
        ` : ''}
        <tr>
          <td>${t("Total")}</td>
          <td style="text-align: right;">${formatCurrency(parseFloat(order.payable_amount || 0))}</td>
        </tr>
      </tbody>
    </table>
  `;
};

/**
 * Generate footer section
 */
const generateFooter = (siteTitle, companyEmail, companyPhone, orderData, t, isRTL) => {
  const safeText = (value) => escapeHtml(value || '');
  
  return `
    <div class="invoice-footer" style="margin-top: 16px; padding-top: 12px; border-top: 2px solid #333; text-align: center; color: #555; font-size: 9pt; page-break-inside: avoid;">
      <div style="margin-bottom: 6px;">
        <strong>${safeText(siteTitle)}</strong>
      </div>
      <div style="margin-bottom: 4px;">
        ${safeText(companyEmail)} | ${safeText(companyPhone)}
      </div>
      <div style="margin-top: 10px; font-size: 10pt; color: #333;">
        ${t("Thank you for your business!")}
      </div>
      ${orderData?.notes ? `
        <div style="margin-top: 12px; padding: 10px; background: #f5f5f5; border-radius: 4px; text-align: ${isRTL ? 'right' : 'left'}; font-size: 9pt;">
          <strong style="color: #333;">${t("Notes")}:</strong>
          <p style="margin: 4px 0 0 0; color: #555;">${safeText(orderData.notes)}</p>
        </div>
      ` : ''}
    </div>
  `;
};

/**
 * A4 dimensions: 210mm × 297mm. Content area with safe margins: ~186mm × 267mm
 */
const A4_CONTENT_WIDTH_MM = 186;
const A4_SAFE_MARGIN_MM = 12;

/**
 * Generate CSS styles optimized for A4 print
 */
const generateStyles = (isRTL) => {
  return `
    * {
      box-sizing: border-box;
    }
    @page {
      size: A4 portrait;
      margin: ${A4_SAFE_MARGIN_MM}mm;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: ${isRTL ? "'Cairo', 'Tajawal', " : ""}'Arial', sans-serif;
      font-size: 11pt;
      line-height: 1.4;
      color: #222;
      background: #fff;
      direction: ${isRTL ? 'rtl' : 'ltr'};
      text-align: ${isRTL ? 'right' : 'left'};
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .invoice-print-sheet {
      max-width: ${A4_CONTENT_WIDTH_MM}mm;
      margin: 0 auto;
      padding: 8mm 0;
      overflow: hidden;
    }
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 18px;
      padding-bottom: 12px;
      border-bottom: 2px solid #333;
      page-break-after: avoid;
      page-break-inside: avoid;
    }
    .company-info img {
      max-width: 45mm;
      max-height: 25mm;
      margin-bottom: 8px;
      object-fit: contain;
    }
    .company-info img[src=""], .company-info img:not([src]) {
      display: none;
    }
    .invoice-info {
      text-align: ${isRTL ? 'left' : 'right'};
    }
    [dir="rtl"] .invoice-info {
      text-align: left;
    }
    [dir="ltr"] .invoice-info {
      text-align: right;
    }
    .invoice-info h2 {
      font-size: 18pt;
      font-weight: bold;
      margin: 0 0 6px 0;
      text-transform: uppercase;
    }
    .info-cards {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 18px;
      page-break-inside: avoid;
    }
    .info-card {
      border: 1px solid #ddd;
      padding: 10px;
      border-radius: 4px;
    }
    .info-card h6 {
      font-size: 9pt;
      font-weight: bold;
      text-transform: uppercase;
      margin: 0 0 6px 0;
      color: #555;
    }
    .invoice-print-sheet table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
      font-size: 9pt;
      table-layout: fixed;
    }
    .invoice-print-sheet table thead {
      display: table-header-group;
    }
    .invoice-print-sheet table th,
    .invoice-print-sheet table td {
      border: 1px solid #ddd;
      padding: 6px 8px;
      text-align: left;
      overflow: hidden;
      word-wrap: break-word;
    }
    .invoice-print-sheet thead th {
      background-color: #f5f5f5;
      font-weight: bold;
      font-size: 9pt;
    }
    .invoice-print-sheet tbody td {
      font-size: 9pt;
    }
    .summary-table {
      width: 55%;
      margin-left: auto;
      margin-right: 0;
      border: none;
      table-layout: auto;
    }
    [dir="rtl"] .summary-table {
      margin-left: 0;
      margin-right: auto;
    }
    .summary-table td {
      border: none;
      padding: 4px 0;
      font-size: 10pt;
    }
    .summary-table tr:last-child td {
      font-size: 11pt;
      font-weight: bold;
      border-top: 2px solid #333;
      padding-top: 8px;
    }
    .invoice-status-row {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 6px 0;
      margin-top: 8px;
    }
    .status-label {
      font-size: 9pt;
      color: #444;
      font-weight: 600;
    }
    .badge {
      display: inline-block;
      padding: 4px 10px;
      border-radius: 4px;
      font-size: 9pt;
      font-weight: 600;
      border: 1px solid #333;
      background-color: #f0f0f0;
      color: #222;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .badge-order {
      background-color: #e8e8e8;
      color: #222;
      border-color: #666;
    }
    .badge-payment {
      background-color: #d8e8d8;
      color: #1a3d1a;
      border-color: #4a7c4a;
    }
    .text-muted {
      color: #555;
    }
    .fw-semibold {
      font-weight: 600;
    }
    .fw-bold {
      font-weight: 700;
    }
    @media print {
      html, body {
        padding: 0;
        margin: 0;
        background: #fff;
        width: 100%;
      }
      .invoice-print-sheet {
        max-width: none;
        width: 100%;
        padding: 0;
      }
      .no-print {
        display: none !important;
      }
      .invoice-header,
      .info-cards {
        page-break-inside: avoid;
      }
      table {
        page-break-inside: auto;
      }
      tr {
        page-break-inside: avoid;
        page-break-after: auto;
      }
    }
    @media screen {
      body {
        padding: 12px;
        background: #e8e8e8;
      }
      .invoice-print-sheet {
        background: #fff;
        padding: 12mm 10mm;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
    }
  `;
};

/**
 * Generate error page when invoice generation fails
 */
const generateErrorPage = (t, lang) => {
  const errorTitle = `Invoice-Error-${new Date().toISOString().split('T')[0]}`;
  
  return `
    <!DOCTYPE html>
    <html lang="${lang}">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${errorTitle}</title>
      <style>
        body {
          font-family: Arial, sans-serif;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          margin: 0;
          background: #f5f5f5;
        }
        .error-container {
          background: white;
          padding: 40px;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          text-align: center;
          max-width: 500px;
        }
        .error-icon {
          font-size: 48px;
          color: #dc3545;
          margin-bottom: 20px;
        }
        h2 {
          color: #333;
          margin-bottom: 15px;
        }
        p {
          color: #666;
          line-height: 1.6;
        }
        button {
          margin-top: 20px;
          padding: 10px 30px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-size: 14px;
        }
        button:hover {
          background: #0056b3;
        }
      </style>
    </head>
    <body>
      <div class="error-container">
        <div class="error-icon">⚠️</div>
        <h2>${t("Error generating invoice")}</h2>
        <p>${t("An error occurred while preparing your invoice for printing. Please try again or contact support if the problem persists.")}</p>
        <button onclick="window.close()">${t("Close Window")}</button>
      </div>
      <script>
        document.title = '${errorTitle}';
      </script>
    </body>
    </html>
  `;
};

